'use client';

import React, { useState, useMemo, useEffect } from 'react';
import { cn } from '../../utils/tailwindUtils';
import { Button } from './button';
import { Input } from './input';
import useApiMethods from '../../lib/useApiMethods';
import { getCSVData, getPrintData } from '../common/CommonFunctions';
import { Tooltip, TooltipContent, TooltipTrigger } from './tooltip';
import { Checkbox, DatePicker, Select } from 'antd';
import dayjs from 'dayjs';
import { Upload, X } from 'lucide-react';
import ToastMsg from '../common/ToastMsg';
import { CUSTOMERBULKUPLOAD, CUSTOMERSNAMEQUERY, LEADFORMLIST } from '../../constants/api';

// --- Reusable SVG Icons ---

const SearchIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4 text-gray-400"><path d="m21 21-4.3-4.3" /><circle cx="11" cy="11" r="8" /></svg>
);

const FilterIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" /></svg>
);

const ExcelIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4"><path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z" /><path d="M14 2v4a2 2 0 0 0 2 2h4" /><path d="M8 13h2" /><path d="M14 13h2" /><path d="M8 17h2" /><path d="M14 17h2" /></svg>
);

const PDFIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4"><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" /><path d="M6 9V3a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v6" /><rect x="6" y="14" width="12" height="8" rx="1" /></svg>
);

const DraftIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4"><path d="M12 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.375 2.625a1 1 0 0 1 3 3l-9.013 9.014a2 2 0 0 1-.853.505l-2.873.84a.5.5 0 0 1-.62-.62l.84-2.873a2 2 0 0 1 .506-.852z" /></svg>
);

const SortIcon = ({ direction }) => {
    if (direction === 'none') {
        return <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4 ml-2 opacity-30"><path d="m7 15-3 3 3 3" /><path d="m20 9-3-3-3 3" /><path d="M4 18v-6a4 4 0 0 1 4-4h12" /><path d="M17 6v6a4 4 0 0 1-4 4H4" /></svg>;
    }
    if (direction === 'asc') {
        return <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4 ml-2"><path d="m3 16 4 4 4-4" /><path d="M7 20V4" /><path d="m21 8-4-4-4 4" /><path d="M17 4v16" /></svg>;
    }
    return <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4 ml-2"><path d="m3 8 4-4 4 4" /><path d="M7 4v16" /><path d="m21 16-4 4-4-4" /><path d="M17 20V4" /></svg>;
};

const ChevronLeftIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5"><path d="m15 18-6-6 6-6" /></svg>
);

const ChevronRightIcon = ({ className = "" }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`h-5 w-5 ${className}`}><path d="m9 18 6-6-6-6" /></svg>
);

const ChevronsLeftIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5"><path d="m11 17-5-5 5-5" /><path d="m18 17-5-5 5-5" /></svg>
);

const ChevronsRightIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5"><path d="m6 17 5-5-5-5" /><path d="m13 17 5-5-5-5" /></svg>
);

const EmptyIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5"><path d="m16 22-1-4" /><path d="M19 13.99a1 1 0 0 0 1-1V12a2 2 0 0 0-2-2h-3a1 1 0 0 1-1-1V4a2 2 0 0 0-4 0v5a1 1 0 0 1-1 1H6a2 2 0 0 0-2 2v.99a1 1 0 0 0 1 1" /><path d="M5 14h14l1.973 6.767A1 1 0 0 1 20 22H4a1 1 0 0 1-.973-1.233z" /><path d="m8 22 1-4" /></svg>
);

const CloseIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
);

// --- Right Sidebar Filter Component ---
export const FilterSidebar = ({
    pagename,
    isOpen,
    onClose,
    filterableColumns,
    tempFilters,
    setTempFilters,
    onApply,
    onClear,
    filterData
}) => {
    const { fetch } = useApiMethods();
    const customerTypeOptions = [
        { value: 'Hospital', label: 'Hospital' },
        { value: 'Clinic', label: 'Clinic' },
        { value: 'Radiology', label: 'Radiology' },
        { value: 'Lab', label: 'Lab' }
    ];

    // Lead stage options
    const leadStageOptions = [
        { value: 'New Lead', label: 'New Lead' },
        { value: 'Unanswered', label: 'Unanswered' },
        { value: 'Contacted', label: 'Contacted' },
        { value: 'Qualified', label: 'Qualified' },
        { value: 'Dis-Qualified', label: 'Dis-Qualified' },
        { value: 'Nurture Lead', label: 'Nurture Lead' },
        { value: 'Demo Fixed', label: 'Demo Fixed' },
        { value: 'Demo Done', label: 'Demo Done' },
        { value: 'Proposal', label: 'Proposal' },
        { value: 'Negotiation', label: 'Negotiation' },
        { value: 'Closed Won', label: 'Closed Won' },
        { value: 'Closed Lost', label: 'Closed Lost' },
        { value: 'On Hold', label: 'On Hold' },
        { value: 'No Show', label: 'No Show' },
    ];

    const [customerNameOptions, setCustomerNameOptions] = useState([])
    const [leadFormOptions, setLeadFormOptions] = useState([])

    const fetchLeadForm = async () => {
        try {
            const response = await fetch(LEADFORMLIST)
            setLeadFormOptions(response.data.map(item => ({ value: item.id, label: item.form_name })));
        } catch (error) {
            console.log(error)
        }
    }

    useEffect(() => {
        if (window.location.pathname === "/reports/lead-sources") {
            fetchLeadForm()
        }
    }, [tempFilters])

    const handleCustomerNameSearch = async (value) => {

        if (value.trim().length > 0) {
            const response = await fetch(`${CUSTOMERSNAMEQUERY}${value}`);
            setCustomerNameOptions(response.data.map(item => ({ value: item.id, label: item.customer_name })));
        }
        else {
            setCustomerNameOptions([])
        }

    }

    const datePresetOptions = [
        { value: 'all',        label: 'All' },
        { value: 'today',      label: 'Today' },
        { value: 'yesterday',  label: 'Yesterday' },
        { value: 'last_week',  label: 'Last Week' },
        { value: 'last_month', label: 'Last Month' },
        { value: 'custom',     label: 'Custom' },
    ];

    const handleDatePresetChange = (preset) => {
        const today = dayjs().format('YYYY-MM-DD');
        const newFilters = { ...tempFilters, date_filter_type: preset };
        delete newFilters.from_date;
        delete newFilters.to_date;
        if (preset === 'today') {
            newFilters.from_date = today;
            newFilters.to_date = today;
        } else if (preset === 'yesterday') {
            const y = dayjs().subtract(1, 'day').format('YYYY-MM-DD');
            newFilters.from_date = y;
            newFilters.to_date = y;
        } else if (preset === 'last_week') {
            newFilters.from_date = dayjs().subtract(7, 'day').format('YYYY-MM-DD');
            newFilters.to_date = today;
        } else if (preset === 'last_month') {
            newFilters.from_date = dayjs().subtract(30, 'day').format('YYYY-MM-DD');
            newFilters.to_date = today;
        }
        setTempFilters(newFilters);
    };

    // Handle select changes for different filter types
    const handleSelectChange = (accessor, value) => {
        // Handle both undefined and empty strings as clear
        if (value === undefined || value === '') {
            const newFilters = { ...tempFilters };
            delete newFilters[accessor];
            setTempFilters(newFilters);
        } else {
            setTempFilters((prev) => ({
                ...prev,
                [accessor]: value
            }));
        }

    };

    // Handle input changes
    const handleInputChange = (accessor, value) => {
        if (value === '') {
            const newFilters = { ...tempFilters };
            delete newFilters[accessor];
            setTempFilters(newFilters);
        } else {
            setTempFilters((prev) => ({
                ...prev,
                [accessor]: value
            }));
        }
    };

    const handleClear = () => {
        handleSelectChange("customer_search_id", "");
        handleSelectChange("lead_form_id", "");
        // Reset all filters to empty object
        setTempFilters({});
        // Also clear any Select component internal state
        onClear();
        fetchLeadForm()
        // Force a re-render by closing and reopening sidebar
        setTimeout(() => {
            onClear();
        }, 100);
    };

    const getFilterKey = (accessor) => {
        const filterMapping = {
            'assigned_to_name': 'assigned_to_id',
            'current_assignee': 'current_assignee_id',
            'user_name': 'user_id',
            'status': 'lead_stage'
        };
        return filterMapping[accessor] || accessor;
    };

    // Helper function to get current value for Select components
    const getSelectValue = (accessor) => {
        const filterKey = getFilterKey(accessor);
        return tempFilters[filterKey] || undefined; // Use undefined for AntD Select to clear
    };

    return (
        <div
            className={cn(
                "fixed top-0 right-0 h-full w-80 shadow-lg transform transition-transform duration-300 ease-in-out z-50 border-l",
                "bg-white border-gray-200 text-gray-800", // light theme
                "dark:bg-gray-900 dark:border-gray-700 dark:text-gray-100", // dark theme
                isOpen ? "translate-x-0" : "translate-x-full"
            )}
        >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-semibold">Filter Data</h3>
                <button
                    onClick={onClose}
                    className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors"
                >
                    <X className="h-5 w-5" />
                </button>
            </div>

            {/* Content */}
            <div className="p-4 h-[calc(100%-120px)] overflow-y-auto">
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
                    Filter data by specific columns.
                </p>
                <div className="space-y-4">
                    {filterableColumns.map(({ accessor, Header }) => {
                        // Determine the actual filter key for this column
                        const filterKey = getFilterKey(accessor);

                        return (
                            <div key={accessor} className="flex flex-col gap-2">
                                <label
                                    htmlFor={accessor}
                                    className="text-xs font-medium text-gray-700 dark:text-gray-300"
                                >
                                    {Header}
                                </label>

                                {accessor === "is_active" ? (
                                    // Custom filter for Status
                                    <div className="flex gap-2">
                                        <button
                                            onClick={() => handleSelectChange("is_active", "True")}
                                            className={`px-3 py-1 rounded text-xs font-medium border transition-colors
                                            ${tempFilters["is_active"] === "True"
                                                    ? "bg-green-100 text-green-700 border-green-300 dark:bg-green-900 dark:text-green-300"
                                                    : "bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700"
                                                }`}
                                        >
                                            Active
                                        </button>
                                        <button
                                            onClick={() => handleSelectChange("is_active", "False")}
                                            className={`px-3 py-1 rounded text-xs font-medium border transition-colors
                                            ${tempFilters["is_active"] === "False"
                                                    ? "bg-red-100 text-red-700 border-red-300 dark:bg-red-900 dark:text-red-300"
                                                    : "bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700"
                                                }`}
                                        >
                                            Inactive
                                        </button>
                                        {/* Clear button for status */}
                                        {/* {(tempFilters["is_active"] === "True" || tempFilters["is_active"] === "False") && (
                                            <button
                                                onClick={() => handleSelectChange("is_active", undefined)}
                                                className="px-3 py-1 rounded text-xs font-medium bg-gray-100 text-gray-600 hover:bg-gray-200"
                                            >
                                                Clear
                                            </button>
                                        )} */}
                                    </div>
                                ) : accessor === "assigned_to_name" || accessor === "current_assignee" || accessor === 'user_name' ? (
                                    <div className="flex gap-2">
                                        <Select
                                            size='large'
                                            placeholder="Select user to assign"
                                            value={getSelectValue(accessor)}
                                            onChange={(value) => handleSelectChange(filterKey, value)}
                                            options={filterData.users?.map(user => ({
                                                value: user.id,
                                                label: user.label,
                                                emp_code: user.emp_code,
                                                role_type: user.role_type,
                                                email: user.email
                                            })) || []}
                                            className="flex-1"
                                            showSearch
                                            allowClear
                                            filterOption={(input, option) =>
                                                option.label.toLowerCase().includes(input.toLowerCase()) ||
                                                option.emp_code.toLowerCase().includes(input.toLowerCase())
                                            }
                                            optionRender={(option) => (
                                                <div className="flex flex-col">
                                                    <span className="font-medium">{option.data.label}</span>
                                                    <div className="flex gap-2 text-xs text-gray-500">
                                                        <span>{option.data.emp_code}</span>
                                                        <span>•</span>
                                                        <span className="capitalize">{option.data.role_type.replace('_', ' ')}</span>
                                                        <span>•</span>
                                                        <span>{option.data.email}</span>
                                                    </div>
                                                </div>
                                            )}
                                        />
                                        {/* Clear button for Select */}
                                        {/* {tempFilters[filterKey] && (
                                            <button
                                                onClick={() => handleSelectChange(filterKey, undefined)}
                                                className="px-3 py-2 rounded-md border border-gray-300 hover:bg-gray-50"
                                            >
                                                <X className="h-4 w-4" />
                                            </button>
                                        )} */}
                                    </div>
                                ) : accessor === "status" ? (
                                    <div className="flex gap-2">
                                        <Select
                                            id="leadStage"
                                            size="large"
                                            placeholder="Select lead stage"
                                            value={getSelectValue(accessor)}
                                            onChange={(value) => handleSelectChange(filterKey, value)}
                                            options={leadStageOptions}
                                            className="flex-1"
                                            allowClear
                                        />
                                        {/* {tempFilters[filterKey] && (
                                            <button
                                                onClick={() => handleSelectChange(filterKey, undefined)}
                                                className="px-3 py-2 rounded-md border border-gray-300 hover:bg-gray-50"
                                            >
                                                <X className="h-4 w-4" />
                                            </button>
                                        )} */}
                                    </div>
                                ) : accessor === "customer_type" ? (
                                    <div className="flex gap-2">
                                        <Select
                                            id="customerType"
                                            size="large"
                                            placeholder="Select type"
                                            value={tempFilters["customer_type"] || undefined}
                                            onChange={(value) => handleSelectChange("customer_type", value)}
                                            options={customerTypeOptions}
                                            className="flex-1"
                                            allowClear
                                        />
                                        {/* {tempFilters["customer_type"] && (
                                            <button
                                                onClick={() => handleSelectChange("customer_type", undefined)}
                                                className="px-3 py-2 rounded-md border border-gray-300 hover:bg-gray-50"
                                            >
                                                <X className="h-4 w-4" />
                                            </button>
                                        )} */}
                                    </div>
                                ) : accessor === "customer_name" ? (
                                    <div className={window.location.pathname === "/reports/lead-sources" ? "flex flex-col gap-2" : "flex gap-2"}>
                                        <Select
                                            showSearch
                                            size="large"
                                            placeholder="Select type"
                                            value={tempFilters["customer_search_id"] || undefined}
                                            onSearch={(value) => handleCustomerNameSearch(value)}
                                            onChange={(value) => handleSelectChange("customer_search_id", value)}
                                            onBlur={() => setCustomerNameOptions([])}
                                            options={customerNameOptions}
                                            className="flex-1"
                                            allowClear
                                            filterOption={false}
                                            optionRender={(option) => (
                                                <div>
                                                    <span>{option.data.label}</span>
                                                </div>
                                            )}
                                        />
                                        {
                                            window.location.pathname === "/reports/lead-sources" && (
                                                <div className='flex flex-col gap-2'>
                                                    <label
                                                        className="text-xs font-medium text-gray-700 dark:text-gray-300"
                                                    >
                                                        Lead Form
                                                    </label>
                                                    <Select
                                                        showSearch
                                                        size="large"
                                                        placeholder="Select type"
                                                        value={tempFilters["lead_form_id"] || undefined}
                                                        onChange={(value) => handleSelectChange("lead_form_id", value)}
                                                        onBlur={() => setLeadFormOptions([])}
                                                        options={leadFormOptions}
                                                        className="flex-1"
                                                        allowClear
                                                        filterOption={false}
                                                        optionRender={(option) => (
                                                            <div>
                                                                <span>{option.data.label}</span>
                                                            </div>
                                                        )}
                                                    />
                                                </div>
                                            )
                                        }
                                        {/* {tempFilters["customer_type"] && (
                                            <button
                                                onClick={() => handleSelectChange("customer_type", undefined)}
                                                className="px-3 py-2 rounded-md border border-gray-300 hover:bg-gray-50"
                                            >
                                                <X className="h-4 w-4" />
                                            </button>
                                        )} */}
                                    </div>
                                ) : accessor === "created_date" ? (
                                    <div className="flex flex-col gap-2">
                                        <Select
                                            size="large"
                                            value={tempFilters.date_filter_type || 'all'}
                                            onChange={handleDatePresetChange}
                                            options={datePresetOptions}
                                            className="w-full"
                                        />
                                        {tempFilters.date_filter_type === 'custom' && (
                                            <div className="flex flex-col gap-2">
                                                <DatePicker
                                                    placeholder="From date"
                                                    format="DD-MM-YYYY"
                                                    size="large"
                                                    value={tempFilters.from_date ? dayjs(tempFilters.from_date) : null}
                                                    onChange={(date) => setTempFilters(prev => ({
                                                        ...prev,
                                                        from_date: date ? date.format('YYYY-MM-DD') : undefined
                                                    }))}
                                                    className="w-full"
                                                    allowClear
                                                />
                                                <DatePicker
                                                    placeholder="To date"
                                                    format="DD-MM-YYYY"
                                                    size="large"
                                                    value={tempFilters.to_date ? dayjs(tempFilters.to_date) : null}
                                                    onChange={(date) => setTempFilters(prev => ({
                                                        ...prev,
                                                        to_date: date ? date.format('YYYY-MM-DD') : undefined
                                                    }))}
                                                    disabledDate={(d) => tempFilters.from_date && d.isBefore(dayjs(tempFilters.from_date), 'day')}
                                                    className="w-full"
                                                    allowClear
                                                />
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    // Default Input for others
                                    <div className="flex gap-2">
                                        <Input
                                            id={accessor}
                                            value={tempFilters[accessor] || ""}
                                            onChange={(e) => handleInputChange(accessor, e.target.value)}
                                            placeholder={`Filter by ${Header}...`}
                                            className="h-9 placeholder:text-xs flex-1"
                                        />
                                    </div>
                                )}
                            </div>
                        )
                    })}
                </div>
            </div>

            {/* Footer */}
            <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900">
                <div className="flex justify-end gap-2">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={handleClear}
                        className="text-gray-700 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white"
                    >
                        Clear All
                    </Button>
                    <Button
                        size="sm"
                        onClick={onApply}
                        className={cn(
                            'bg-primary hover:bg-sidebar-primary',
                            'dark:bg-indigo-600 dark:hover:bg-indigo-700',
                            'text-white transition-colors duration-200'
                        )}
                    >
                        Apply Filters
                    </Button>
                </div>
            </div>
        </div>
    );
};

// --- TableHeader Component ---
export const TableHeader = ({
    pagename,
    tableTitle,
    globalFilter,
    setGlobalFilter,
    children,
    filterableColumns,
    columnFilters,
    onColumnFilterChange,
    onApplyFilters,
    onClearFilters,
    sorting,
    isDraft,
    setIsDraft,
    url,
    isDateRequired = false,
    isCSVRequired = false,
    isPDFRequired = false,
    isDraftRequired = false,
    isMonthYearRequired = false,
    isFromToDateRequired = false,
    date,
    setDate,
    month,
    setMonth,
    year,
    setYear,
    fromDate,
    setFromDate,
    toDate,
    setToDate,
    filterData,
    setRefresh,
    bulkUploadRequired,
    selectedRows,
    setShowAssigneeLogForUser,
    leftToolbarContent = null,
}) => {
    const { create } = useApiMethods();
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [tempFilters, setTempFilters] = useState(columnFilters);

    const isFilterActive = useMemo(() => Object.values(columnFilters).some(Boolean), [columnFilters]);

    const handleApply = () => {
        onApplyFilters(tempFilters);
        setSidebarOpen(false);
    };

    const handleClear = () => {
        setTempFilters({});
        onClearFilters();
    };

    useEffect(() => {
        setTempFilters(columnFilters);
    }, [columnFilters]);

    const handleExport = (type) => {
        const filters = {
            search: globalFilter || "",
            ...columnFilters,
            column: sorting?.accessor || "",
            sort: sorting?.direction !== "none" ? sorting.direction : "",
            date: date ? dayjs(date).format("YYYY-MM-DD") : "",
            month: month ? dayjs(month).format("M") : "",
            year: year ? dayjs(year).format("YYYY") : "",
        };

        if (isMonthYearRequired) {
            delete filters['date']
        }

        if (isFromToDateRequired) {
            delete filters['date']
            delete filters['month']
            delete filters['year']
        }

        switch (type) {
            case "pdf":
                getPrintData(url, filters);
                break;
            case "csv":
            case "excel":
                getCSVData(url, filters, type);
                break;
            default:
                console.warn(`Unsupported export type: ${type}`);
        }
    };

    const toggleDraft = () => {
        setIsDraft((prev) => !prev);
    };

    const uploadExcel = async () => {
        try {
            // Create a file input element dynamically
            const input = document.createElement('input');
            input.type = 'file';
            input.accept = '.xlsx, .xls, .csv'; // Accept Excel and CSV files
            input.style.display = 'none';

            // Add change event listener
            input.onchange = async (event) => {
                const file = event.target.files[0];

                if (!file) {
                    return; // User cancelled
                }

                // Validate file type
                const validTypes = [
                    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                    'application/vnd.ms-excel',
                    'text/csv'
                ];

                if (!validTypes.includes(file.type) && !file.name.match(/\.(xlsx|xls|csv)$/)) {
                    ToastMsg('error', 'Please upload only Excel or CSV files');
                    return;
                }

                // Validate file size (e.g., 10MB limit)
                const maxSize = 10 * 1024 * 1024; // 10MB
                if (file.size > maxSize) {
                    ToastMsg('error', 'File size should not exceed 10MB');
                    return;
                }

                // Show loading state
                ToastMsg('loading', 'Uploading file...');

                // Create FormData and append the file
                const formData = new FormData();
                formData.append('file', file);

                try {
                    setRefresh(true)
                    const response = await create(CUSTOMERBULKUPLOAD, formData, true);

                    // Handle the response data if needed
                    if (response.status !== 'fail') {
                        console.log('Upload response:', response);

                        // If there are stats or results, you can show them
                        if (response.status === 'success') {
                            // The server says what it did with rows whose number was
                            // already a lead (created / added / reopened / skipped).
                            ToastMsg('success', response.message || 'File uploaded successfully!');
                            setRefresh(false)
                        } else {
                            ToastMsg('error', response.message || 'Upload failed');
                        }
                    } else {
                        ToastMsg('error', response.message || 'Upload failed');
                    }
                } catch (error) {
                    ToastMsg('error', error.message || 'An error occurred during upload');
                    console.error('Upload error:', error);
                } finally {
                    // Clean up the input element
                    document.body.removeChild(input);
                }
            };

            // Add to DOM and trigger click
            document.body.appendChild(input);
            input.click();

        } catch (error) {
            ToastMsg('error', error.message || 'Failed to start upload');
            console.error('Upload initialization error:', error);
        }
    };

    return (
        <div className="p-2 bg-gray-50 dark:bg-gray-900 rounded-t-lg border-b border-gray-200 dark:border-gray-700">
            <div className="flex flex-col md:flex-row items-center justify-between gap-2">
                {/* <h2 className="text-xl font-semibold text-gray-800">{tableTitle}</h2> --> Title moved to parent component */}
                <div className="flex md:flex-row items-center gap-2 w-full md:w-auto">
                    {/* Excel Button - Green theme */}
                    {
                        isCSVRequired && (
                            <Tooltip>
                                <TooltipTrigger>
                                    <button
                                        className="flex items-center justify-center w-10 h-10 rounded-md shadow-sm text-xs font-medium
                                bg-green-50 text-green-700 border border-green-200 
                                hover:bg-green-100 hover:border-green-300 hover:text-green-800
                                transition-colors duration-200
                                dark:bg-green-900/30 dark:text-green-300 dark:border-green-700
                                dark:hover:bg-green-900/50 dark:hover:text-green-200 dark:hover:border-green-600"
                                        onClick={() => handleExport('excel')}
                                    >
                                        <ExcelIcon />
                                    </button>
                                </TooltipTrigger>
                                <TooltipContent side="bottom" sideOffset={5} transition={false} className="text-xs">
                                    Export to CSV
                                </TooltipContent>
                            </Tooltip>
                        )
                    }

                    {/* PDF Button - Red theme */}
                    {
                        isPDFRequired && (
                            <Tooltip>
                                <TooltipTrigger>
                                    <button
                                        className="flex items-center justify-center w-10 h-10 rounded-md shadow-sm text-xs font-medium
                                bg-red-50 text-red-700 border border-red-200 
                                hover:bg-red-100 hover:border-red-300 hover:text-red-800
                                transition-colors duration-200
                                dark:bg-red-900/30 dark:text-red-300 dark:border-red-700
                                dark:hover:bg-red-900/50 dark:hover:text-red-200 dark:hover:border-red-600"
                                        onClick={() => handleExport("pdf")}
                                    >
                                        <PDFIcon />
                                    </button>
                                </TooltipTrigger>
                                <TooltipContent side="bottom" sideOffset={5} transition={false} className="text-xs">
                                    Export to PDF
                                </TooltipContent>
                            </Tooltip>
                        )
                    }

                    {/* Draft Button - Blue theme */}
                    {
                        isDraftRequired && (
                            <Tooltip>
                                <TooltipTrigger>
                                    <button
                                        className={cn("flex items-center justify-center w-10 h-10 rounded-md shadow-sm text-xs font-medium",
                                            "bg-blue-50 text-blue-700 border border-blue-200",
                                            "hover:bg-blue-100 hover:border-blue-300 hover:text-blue-800",
                                            "transition-colors duration-200",
                                            "dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-700",
                                            "dark:hover:bg-blue-900/50 dark:hover:text-blue-200 dark:hover:border-blue-600"
                                            , isDraft ? "ring-2 ring-offset-2 ring-blue-500" : "")}
                                        onClick={toggleDraft}
                                    >
                                        <DraftIcon />
                                    </button>
                                </TooltipTrigger>
                                <TooltipContent side="bottom" sideOffset={5} transition={false} className="text-xs">
                                    Draft
                                </TooltipContent>
                            </Tooltip>
                        )
                    }

                    {/* Bulk upload */}
                    {
                        bulkUploadRequired && (
                            <Tooltip>
                                <TooltipTrigger>
                                    <button
                                        className={cn("flex items-center justify-center w-10 h-10 rounded-md shadow-sm text-xs font-medium",
                                            "bg-blue-50 text-blue-700 border border-blue-200",
                                            "hover:bg-blue-100 hover:border-blue-300 hover:text-blue-800",
                                            "transition-colors duration-200",
                                            "dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-700",
                                            "dark:hover:bg-blue-900/50 dark:hover:text-blue-200 dark:hover:border-blue-600"
                                            , isDraft ? "ring-2 ring-offset-2 ring-blue-500" : "")}
                                        onClick={uploadExcel}
                                    >
                                        <Upload className='w-5 h-5' />
                                    </button>
                                </TooltipTrigger>
                                <TooltipContent side="bottom" sideOffset={5} transition={false} className="text-xs">
                                    Bulk Upload
                                </TooltipContent>
                            </Tooltip>
                        )
                    }
                </div>
                <div className="flex md:flex-row items-center gap-2 w-full md:w-auto">
                    {/* date */}
                    {
                        // based on the isMonthYearRequired or isDateRequired we need to change the format of a datePicker like normal and year and month
                        isDateRequired ? (
                            <DatePicker
                                value={date ? dayjs(date, 'YYYY-MM-DD') : null}
                                onChange={(dateObj, dateString) => setDate(dateString || dayjs().format('YYYY-MM-DD'))}
                                format="YYYY-MM-DD"
                                picker={'date'}
                                allowClear={false}
                                className='w-32'
                            // disabledDate={(current) => {
                            //     // Disable past dates : hide for later use
                            //     return current && current < dayjs().startOf("day");
                            // }}
                            />
                        ) :

                            isMonthYearRequired ? (
                                <>
                                    {/* Month Picker */}
                                    <DatePicker
                                        picker="month"
                                        value={month}
                                        onChange={(dateObj) => setMonth(dateObj)}
                                        format="MMM"
                                        className="w-20"
                                    />

                                    {/* Year Picker */}
                                    <DatePicker
                                        picker="year"
                                        value={year}
                                        onChange={(dateObj) => setYear(dateObj)}
                                        format="YYYY"
                                        className="w-20"
                                    />
                                </>
                            ) :
                                isFromToDateRequired ? (
                                    <>
                                        {/* From Date Picker */}
                                        <DatePicker
                                            picker="date"
                                            value={fromDate}
                                            onChange={(dateObj) => setFromDate(dateObj)}
                                            format="YYYY-MM-DD"
                                            className="w-32"
                                        />

                                        {/* To Date Picker */}
                                        <DatePicker
                                            picker="date"
                                            value={toDate}
                                            onChange={(dateObj) => setToDate(dateObj)}
                                            format="YYYY-MM-DD"
                                            className="w-32"
                                        />
                                    </>
                                ) : ''
                    }
                    {
                        selectedRows?.length > 0 && (
                            <Button
                                size="lg"
                                className={cn(
                                    "bg-primary hover:bg-sidebar-primary",
                                    "dark:bg-sidebar-primary dark:hover:bg-sidebar-primary/90",
                                    "text-white transition-colors duration-200",
                                    "shadow-sm hover:shadow-md",
                                    "whitespace-nowrap"
                                )}
                                onClick={() => setShowAssigneeLogForUser(true)}
                                aria-label={'Assign to User'}
                            >
                                Assign to User
                            </Button>
                        )
                    }
                    {leftToolbarContent}
                    <div className="relative w-full md:w-64">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <SearchIcon />
                        </div>
                        <input
                            type="text"
                            value={globalFilter || ''}
                            onChange={e => setGlobalFilter(e.target.value)}
                            className="block w-full pl-10 pr-3 py-3 border rounded-md leading-5 
                            bg-white text-gray-900 placeholder-gray-500 
                            focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-primary focus:border-sidebar-primary 
                            sm:text-xs dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600 dark:placeholder-gray-400"
                            placeholder="Search table..."
                        />
                    </div>
                    {filterableColumns.length > 0 && (
                        <>
                            <Tooltip>
                                <TooltipTrigger>
                                    <button
                                        onClick={() => setSidebarOpen(true)}
                                        className="relative flex items-center justify-center w-10 h-10 border rounded-md shadow-sm 
                                        text-xs font-medium 
                                        text-gray-700 bg-white hover:bg-gray-50 border-gray-300
                                        dark:text-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 dark:border-gray-600"
                                    >
                                        <FilterIcon />
                                        {isFilterActive && (
                                            <span className="absolute -top-1 -right-1 block h-2.5 w-2.5 rounded-full bg-indigo-600 ring-2 ring-white" />
                                        )}
                                    </button>
                                </TooltipTrigger>
                                <TooltipContent side="bottom" sideOffset={5} transition={false} className="text-xs">
                                    Filter
                                </TooltipContent>
                            </Tooltip>

                            <FilterSidebar
                                pagename={pagename}
                                isOpen={sidebarOpen}
                                onClose={() => setSidebarOpen(false)}
                                filterableColumns={filterableColumns}
                                tempFilters={tempFilters}
                                setTempFilters={setTempFilters}
                                onApply={handleApply}
                                onClear={handleClear}
                                filterData={filterData}
                            />
                        </>
                    )}
                </div>
            </div>
            {children && <div className="mt-4">{children}</div>}
        </div>
    );
};

// --- DataTable Component (Renamed to Reactable) ---
export const Reactable = ({
    pagename = "",
    columns,
    data,
    setWholeResponse = null,
    selectedDate,
    selectedMonth,
    selectedRows,
    setSelectedRows,
    setShowAssigneeLogForUser = false,
    setData,
    setTotalCount,
    filter = {},
    setFetchFilter = () => { },
    filterData = {},
    tableTitle = "Data",
    renderSubComponent,
    children,
    tableHeight,
    rowSize = 'md',
    onRowClick,
    onRowDoubleClick,
    onRowHighlightKey = null,
    url,
    serverSide = false,
    serverTrigger = false,
    isDateRequired = false,
    isCSVRequired = false,
    isPDFRequired = false,
    isDraftRequired = false,
    isMonthYearRequired = false,
    isFromToDateRequired = false,
    bulkUploadRequired = false,
    leftToolbarContent = null,
}) => {
    const storageKey = url ? `reactable_state_${url}` : null;
    const [globalFilter, setGlobalFilter] = useState(() => {
        if (storageKey) {
            try {
                const saved = sessionStorage.getItem(storageKey);
                if (saved) return JSON.parse(saved).globalFilter || '';
            } catch {}
        }
        return '';
    });
    const stringifiedFilter = JSON.stringify(filter);
    const [columnFilters, setColumnFilters] = useState(() => {
        if (storageKey) {
            try {
                const saved = sessionStorage.getItem(storageKey);
                if (saved) return JSON.parse(saved).columnFilters || {};
            } catch {}
        }
        return {};
    });
    const [sorting, setSorting] = useState({ accessor: '', direction: 'none' });
    const [pagination, setPagination] = useState(() => {
        if (pagename) {
            const saved = sessionStorage.getItem(`reactable_pagination_${pagename}`);
            if (saved) {
                try {
                    return JSON.parse(saved);
                } catch (e) {
                    console.error("Failed to parse pagination from session storage", e);
                }
            }
        }
        return { pageIndex: 0, pageSize: 50 };
    });
    const [jumpToPage, setJumpToPage] = useState('');
    const [expandedRows, setExpandedRows] = useState({});
    const [totalRecords, setTotalRecords] = useState(0);
    const [isDraft, setIsDraft] = useState(false);
    const [loading, setLoading] = useState(false);

    const [refresh, setRefresh] = useState(false);
    const [date, setDate] = useState(() => {
        if (storageKey) {
            try {
                const saved = sessionStorage.getItem(storageKey);
                if (saved) return JSON.parse(saved).date || dayjs().format('YYYY-MM-DD');
            } catch {}
        }
        return dayjs().format('YYYY-MM-DD');
    });

    // State
    const [month, setMonth] = useState(
        selectedMonth ? selectedMonth : dayjs()
    );
    const [year, setYear] = useState(dayjs());

    const [fromDate, setFromDate] = useState(
        selectedDate ? selectedDate?.from_date : dayjs()
    );
    const [toDate, setToDate] = useState(
        selectedDate ? selectedDate?.to_date : dayjs()
    );

    // Persist pagination to session storage
    useEffect(() => {
        if (pagename) {
            sessionStorage.setItem(`reactable_pagination_${pagename}`, JSON.stringify(pagination));
        }
    }, [pagination, pagename]);

    // Persist search text, date filter, and column filters to session storage
    useEffect(() => {
        if (storageKey) {
            try {
                sessionStorage.setItem(storageKey, JSON.stringify({ globalFilter, date, columnFilters }));
            } catch {}
        }
    }, [globalFilter, date, columnFilters, storageKey]);

    const { fetch } = useApiMethods();
    const filterableColumns = useMemo(() => columns.filter(c => c.filterable), [columns]);

    // Function to determine row highlight class
    const getRowHighlightClass = (row) => {

        if (!onRowHighlightKey) return '';

        let shouldHighlight = false;

        if (typeof onRowHighlightKey === 'function') {
            // Custom function provided
            shouldHighlight = onRowHighlightKey(row);
        } else {
            // String key provided - check if the value is truthy
            shouldHighlight = !!row[onRowHighlightKey];
        }

        return shouldHighlight
            ? 'bg-green-100 dark:bg-green-900'
            : '';
    };

    useEffect(() => {
        if (selectedMonth && isMonthYearRequired) {
            setMonth(selectedMonth);
            setYear(selectedMonth);
        }
    }, [selectedMonth, isMonthYearRequired]);

    // Server-side data fetching
    useEffect(() => {
        if (serverSide && url) {
            const fetchData = async () => {
                try {
                    setLoading(true);

                    // Build filters object for API
                    // NOTE: ...columnFilters must come LAST among the spread sources so sidebar
                    // values (e.g. from_date/to_date set by the date-preset picker) are not
                    // overridden by the fromDate/toDate state (which defaults to today).
                    // fromDate/toDate state is only used when isFromToDateRequired=true (those
                    // pages have a dedicated date-range picker outside the sidebar).
                    const filters = {
                        search: globalFilter || "",
                        ...filter,
                        column: sorting.accessor || "",
                        sort: sorting.direction !== 'none' ? sorting.direction : "desc",
                        page_no: pagination.pageIndex + 1,
                        page_size: pagination.pageSize,
                        is_draft: isDraft ? "true" : "",
                        date: date ? dayjs(date).format("YYYY-MM-DD") : "",
                        month: month ? dayjs(month).format("M") : "",
                        year: year ? dayjs(year).format("YYYY") : "",
                        ...(isFromToDateRequired ? {
                            from_date: fromDate ? dayjs(fromDate).format('YYYY-MM-DD') : "",
                            to_date: toDate ? dayjs(toDate).format('YYYY-MM-DD') : "",
                        } : {}),
                        // Spread columnFilters last so sidebar values win over everything above
                        ...columnFilters,
                    };

                    if (isMonthYearRequired) {
                        delete filters['date']
                        delete filters['from_date']
                        delete filters['to_date']
                    }

                    if (isFromToDateRequired) {
                        delete filters['date']
                        delete filters['month']
                        delete filters['year']
                    }

                    if (isDateRequired) {
                        delete filters['from_date']
                        delete filters['to_date']
                    }

                    if (!isDateRequired && !isMonthYearRequired && !isFromToDateRequired) {
                        delete filters['date']
                        delete filters['month']
                        delete filters['year']
                        // from_date/to_date only exist here if columnFilters set them (sidebar),
                        // since we no longer add them from state when !isFromToDateRequired.
                        // Nothing extra to delete — they're either from the sidebar (keep) or absent.
                    }

                    // Remove UI-only keys that must not reach the API
                    delete filters['date_filter_type']

                    // Remove empty filters
                    Object.keys(filters).forEach(key => {
                        if (filters[key] === "" || filters[key] === null || filters[key] === undefined) {
                            delete filters[key];
                        }
                    });

                    console.log('[Reactable] API filters:', filters);
                    const response = await fetch(url, filters);

                    if (response?.data) {
                        setData(response.data);
                        setTotalRecords(response.total_count || 0);
                        setTotalCount(response.total_count || 0);
                        setFetchFilter(filters);
                        setWholeResponse(response);
                    }
                } catch (err) {
                    console.error("Reactable API Error:", err);
                } finally {
                    setLoading(false);
                }
            };

            // Add debounce to prevent too many API calls
            const timeoutId = setTimeout(fetchData, 300);
            return () => clearTimeout(timeoutId);
        }
    }, [
        url,
        serverSide,
        serverTrigger,
        globalFilter,
        columnFilters,
        sorting,
        pagination,
        isDraft,
        date,
        month,
        year,
        fromDate,
        toDate,
        refresh,
        stringifiedFilter
    ]);

    // Filter out columns that are hidden
    const visibleColumns = useMemo(() => columns.filter(c => c.visible !== false), [columns]);

    // Client-side filtering and sorting (for non-serverSide mode)
    const processedData = useMemo(() => {
        if (serverSide) {
            return data; // Use data directly from API when serverSide is true
        }

        let filtered = [...data];


        // Global filter
        if (globalFilter) {
            filtered = filtered.filter(row =>
                columns.some(({ accessor }) => {
                    const value = row[accessor];
                    return String(value).toLowerCase().includes(globalFilter.toLowerCase());
                })
            );
        }

        // Column filters
        Object.entries(columnFilters).forEach(([accessor, filterValue]) => {
            console.log(columnFilters)
            if (filterValue) {
                filtered = filtered.filter(row => {
                    const value = row[accessor];
                    return String(value).toLowerCase().includes(filterValue.toLowerCase());
                });
            }
        });

        // Sorting
        if (sorting.direction !== 'none') {
            filtered = [...filtered].sort((a, b) => {
                const aValue = a[sorting.accessor];
                const bValue = b[sorting.accessor];
                if (aValue < bValue) return sorting.direction === 'asc' ? -1 : 1;
                if (aValue > bValue) return sorting.direction === 'asc' ? 1 : -1;
                return 0;
            });
        }

        return filtered;
    }, [data, columns, globalFilter, columnFilters, sorting, serverSide]);

    const handleAllRowsSelected = (e) => {
        if (e.target.checked) {
            setSelectedRows(prv => [...new Set([...prv, ...processedData.map(row => row.id)])])
        } else {
            setSelectedRows(prv => [...prv.filter(rowId => !processedData.some(row => row.id === rowId))])
        }
    }

    const handleRowSelected = (id) => {
        selectedRows.includes(id) ?
            setSelectedRows(prev => prev.filter(rowId => rowId !== id)) :
            setSelectedRows(prev => [...prev, id])
    }

    // Calculate page count based on server-side or client-side
    const pageCount = serverSide
        ? Math.ceil(totalRecords / pagination.pageSize)
        : Math.ceil(processedData.length / pagination.pageSize);

    // Get data for current page
    const paginatedData = useMemo(() => {
        if (serverSide) {
            return data; // API already returns paginated data
        }

        const start = pagination.pageIndex * pagination.pageSize;
        const end = start + pagination.pageSize;
        return processedData.slice(start, end);
    }, [processedData, pagination, serverSide, data]);

    const handleSort = (accessor) => {
        setSorting(prev => {
            if (prev.accessor === accessor) {
                if (prev.direction === 'asc') return { accessor, direction: 'desc' };
                if (prev.direction === 'desc') return { accessor, direction: 'none' };
            }
            return { accessor, direction: 'asc' };
        });
    };

    const handleJumpToPage = (e) => {
        e.preventDefault();
        const page = parseInt(jumpToPage, 10);
        if (!isNaN(page) && page > 0 && page <= pageCount) {
            setPagination(p => ({ ...p, pageIndex: page - 1 }));
        }
        setJumpToPage('');
    };

    const handlePageSizeChange = (e) => {
        setPagination(p => ({
            ...p,
            pageSize: Number(e.target.value),
            pageIndex: 0
        }));
    };

    const toggleRow = (index) => {
        setExpandedRows(prev => ({
            ...prev,
            [index]: !prev[index]
        }));
    };

    const getAlignmentClass = (align) => {
        switch (align) {
            case 'center': return 'text-center';
            case 'right': return 'text-right';
            default: return 'text-left';
        }
    };

    const getRowSizeClass = () => {
        switch (rowSize) {
            case 'xs': return 'py-1';
            case 'sm': return 'py-2';
            case 'md': return 'py-4';
            case 'lg': return 'py-6';
            default: return 'py-4';
        }
    }

    // Reset to first page when filters change
    useEffect(() => {
        setPagination(p => ({ ...p, pageIndex: 0 }));
    }, [globalFilter, columnFilters, sorting, stringifiedFilter]);

    // Calculate offsets for fixed columns
    const fixedOffsets = useMemo(() => {
        const offsets = {};
        let leftAccumulator = renderSubComponent ? 48 : 0;

        visibleColumns.forEach(col => {
            if (col.fixed && col.align !== 'right') {
                offsets[col.accessor] = { left: leftAccumulator };
                leftAccumulator += (parseInt(col.width) || 150);
            }
        });

        let rightAccumulator = 0;
        [...visibleColumns].reverse().forEach(col => {
            if (col.fixed && col.align === 'right') {
                offsets[col.accessor] = { right: rightAccumulator };
                rightAccumulator += (parseInt(col.width) || 120);
            }
        });

        return offsets;
    }, [visibleColumns, renderSubComponent]);

    return (
        <div className={cn("shadow-lg rounded-lg overflow-hidden border border-gray-200 bg-white flex flex-col dark:bg-gray-900", !tableHeight && "flex-1 min-h-0")}>
            {/* Header */}
            <TableHeader
                pagename={pagename}
                tableTitle={tableTitle}
                globalFilter={globalFilter}
                setGlobalFilter={setGlobalFilter}
                filterableColumns={filterableColumns}
                columnFilters={columnFilters}
                onApplyFilters={setColumnFilters}
                onClearFilters={() => setColumnFilters({})}
                sorting={sorting}
                isDraft={isDraft}
                setIsDraft={setIsDraft}
                url={url}
                isDateRequired={isDateRequired}
                isCSVRequired={isCSVRequired}
                isPDFRequired={isPDFRequired}
                isDraftRequired={isDraftRequired}
                isMonthYearRequired={isMonthYearRequired}
                isFromToDateRequired={isFromToDateRequired}
                date={date}
                setDate={setDate}
                month={month}
                setMonth={setMonth}
                year={year}
                setYear={setYear}
                fromDate={fromDate}
                setFromDate={setFromDate}
                toDate={toDate}
                setToDate={setToDate}
                filterData={filterData}
                setRefresh={setRefresh}
                bulkUploadRequired={bulkUploadRequired}
                selectedRows={selectedRows}
                setShowAssigneeLogForUser={setShowAssigneeLogForUser}
                leftToolbarContent={leftToolbarContent}
            >
                {children}
            </TableHeader>

            {/* Loader */}
            {
                loading ? (
                    <div
                        className={cn("flex justify-center items-center py-8 bg-white dark:bg-gray-900", !tableHeight && "flex-1 min-h-0")}
                        style={tableHeight ? { height: tableHeight } : {}}
                    >
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sidebar-primary"></div>
                    </div>
                ) : (
                    <div
                        className={cn("overflow-x-auto bg-white dark:bg-gray-900", !tableHeight && "flex-1 min-h-0 overflow-y-auto")}
                        style={tableHeight ? { height: tableHeight, overflowY: "auto" } : {}}
                    >
                        {/* Table */}
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                            <thead className="bg-gray-50 dark:bg-gray-800">
                                <tr>
                                    {renderSubComponent && (
                                        <th className="sticky top-0 z-20 w-12 bg-gray-50 dark:bg-gray-800"></th>
                                    )}
                                    {visibleColumns.map(({ accessor, Header, sortable, fixed, align, width, maxWidth, rowselect }) => (
                                        <th
                                            key={accessor}
                                            scope="col"
                                            className={cn(
                                                "px-3 sm:px-6 py-2 capitalize tracking-wider font-medium text-gray-500 dark:text-gray-300 text-xs sticky top-0 z-0 bg-gray-100 dark:bg-gray-800 whitespace-nowrap", // ⬅ prevents wrapping
                                                getAlignmentClass(align),
                                                fixed && "z-[10] sticky",
                                                rowselect && "z-[8] sticky",
                                            )}
                                            style={{
                                                width: width || undefined,
                                                maxWidth: maxWidth || undefined,
                                                minWidth: width || undefined,
                                                ...(fixed ? fixedOffsets[accessor] : {})
                                            }}
                                        >
                                            {sortable ? (
                                                <button
                                                    onClick={() => handleSort(accessor)}
                                                    className={cn(
                                                        "flex items-center gap-1 focus:outline-none",
                                                        align === "center"
                                                            ? "mx-auto"
                                                            : align === "right"
                                                                ? "ml-auto"
                                                                : "mr-auto"
                                                    )}
                                                >
                                                    <span>{Header}</span>
                                                    <SortIcon
                                                        direction={
                                                            sorting.accessor === accessor
                                                                ? sorting.direction
                                                                : "none"
                                                        }
                                                    />
                                                </button>
                                            ) : rowselect ? (
                                                <button>
                                                    <Checkbox checked={processedData.length > 0 && selectedRows.filter(rowId => processedData.some(row => row.id === rowId)).length === processedData.length} onChange={handleAllRowsSelected} />
                                                </button>
                                            ) : (
                                                Header
                                            )}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                                {paginatedData.length > 0 ? (
                                    paginatedData.map((row, i) => {
                                        const highlightClass = getRowHighlightClass(row);

                                        const isFirstNonFixedColumn = (index) => {
                                            const nonFixedColumns = visibleColumns.filter(col => !col.fixed);
                                            return index === (renderSubComponent ? 1 : 0) && nonFixedColumns.length > 0;
                                        };

                                        return (
                                            <React.Fragment key={i}>
                                                <tr
                                                    className={cn(
                                                        "group transition-colors duration-150",
                                                        highlightClass || "hover:bg-gray-50 dark:hover:bg-gray-800"
                                                    )}
                                                    onClick={(e) => onRowClick?.(row, e)}
                                                    onDoubleClick={(e) => onRowDoubleClick?.(row, e)}
                                                >
                                                    {renderSubComponent && (
                                                        <td className={cn(
                                                            "sticky left-0 z-20 px-3 sm:px-6 whitespace-nowrap text-xs text-gray-700 dark:text-gray-300",
                                                            highlightClass ? highlightClass.replace('border-l-4', '') : "bg-white dark:bg-gray-900 group-hover:bg-gray-50 dark:group-hover:bg-gray-800"
                                                        )}>
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    toggleRow(i);
                                                                }}
                                                            >
                                                                <ChevronRightIcon
                                                                    className={`transform transition-transform ${expandedRows[i] ? "rotate-90" : ""
                                                                        }`}
                                                                />
                                                            </button>
                                                        </td>
                                                    )}
                                                    {visibleColumns.map(({ accessor, Cell, fixed, align, rowselect }, colIndex) => (
                                                        <td
                                                            key={accessor}
                                                            className={cn(
                                                                "px-3 sm:px-6 whitespace-nowrap text-[13px] text-gray-700 dark:text-gray-300",
                                                                getRowSizeClass(),
                                                                getAlignmentClass(align),
                                                                fixed && "sticky z-[5] ",
                                                                rowselect && "sticky z-[5]",
                                                                highlightClass ? highlightClass.replace('border-l-4', '') : "group-hover:bg-gray-50 dark:group-hover:bg-gray-800",
                                                                highlightClass ? highlightClass.replace('border-l-4', '') :
                                                                    fixed ? "bg-white dark:bg-gray-900" : ""
                                                            )}
                                                            style={{
                                                                ...(!fixed && isFirstNonFixedColumn(colIndex) && highlightClass.includes('border-l-4')
                                                                    ? {
                                                                        borderLeft: '4px solid #10B981'
                                                                    }
                                                                    : {}),
                                                                ...(fixed ? fixedOffsets[accessor] : {})
                                                            }}
                                                        >
                                                            {Cell ? <Cell value={row[accessor]} row={row} /> : rowselect ? <Checkbox checked={selectedRows.includes(row.id)} onChange={() => handleRowSelected(row.id)} /> : row[accessor]}
                                                        </td>
                                                    ))}
                                                </tr>
                                                {renderSubComponent && expandedRows[i] && (
                                                    <tr className={cn(
                                                        "bg-gray-50 dark:bg-gray-800",
                                                        highlightClass ? highlightClass.replace('border-l-4', '') : ""
                                                    )}>
                                                        <td colSpan={columns.length + 1} className="p-0">
                                                            {renderSubComponent({ row })}
                                                        </td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        );
                                    })
                                ) : (
                                    <tr>
                                        <td
                                            colSpan={columns.length + (renderSubComponent ? 1 : 0)}
                                            className="text-center py-12 sm:py-16"
                                        >
                                            <div className="flex flex-col items-center justify-center text-gray-500 dark:text-gray-400">
                                                <EmptyIcon />
                                                <h3 className="mt-4 text-sm sm:text-lg font-semibold">
                                                    No Records Found
                                                </h3>
                                                <p className="text-xs sm:text-sm">
                                                    There is no data matching your query.
                                                </p>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )
            }

            {/* Pagination */}
            {
                pageCount > 0 && !loading && (
                    <div className="px-3 sm:px-6 py-2 flex flex-col sm:flex-row gap-2 sm:gap-0 sm:items-center sm:justify-between border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
                        {/* Mobile: Prev / Next only */}
                        <div className="flex sm:hidden justify-between">
                            <button
                                onClick={() =>
                                    setPagination((p) => ({ ...p, pageIndex: p.pageIndex - 1 }))
                                }
                                disabled={pagination.pageIndex === 0}
                                className="px-4 py-2 border rounded-md text-xs font-medium bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
                            >
                                Previous
                            </button>
                            <button
                                onClick={() =>
                                    setPagination((p) => ({ ...p, pageIndex: p.pageIndex + 1 }))
                                }
                                disabled={pagination.pageIndex >= pageCount - 1}
                                className="px-4 py-2 border rounded-md text-xs font-medium bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
                            >
                                Next
                            </button>
                        </div>

                        {/* Tablet + Desktop */}
                        <div className="hidden sm:flex flex-1 items-center justify-between">
                            {/* Rows per page */}
                            <div className="flex items-center gap-x-2">
                                <span className="text-xs text-gray-700 dark:text-gray-300">
                                    Rows per page
                                </span>
                                <select
                                    value={pagination.pageSize}
                                    onChange={handlePageSizeChange}
                                    className="block rounded-md p-1 border-gray-300 dark:border-gray-600 shadow-md focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 sm:text-xs dark:bg-gray-800 dark:text-gray-100"
                                >
                                    {[50, 80, 100].map((size) => (
                                        <option key={size} value={size}>
                                            {size}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Page info + navigation */}
                            <div className="flex items-center gap-x-4 lg:gap-x-6">
                                <p className="text-xs text-gray-700 dark:text-gray-300">
                                    Page{" "}
                                    <span className="font-medium">{pagination.pageIndex + 1}</span> of{" "}
                                    <span className="font-medium">{pageCount}</span>
                                </p>

                                {/* Jump to page (desktop only) */}
                                <form
                                    onSubmit={handleJumpToPage}
                                    className="hidden lg:flex items-center gap-x-2"
                                >
                                    <label
                                        htmlFor="jump-to-page"
                                        className="text-xs text-gray-700 dark:text-gray-300"
                                    >
                                        Go to page:
                                    </label>
                                    <input
                                        id="jump-to-page"
                                        type="number"
                                        value={jumpToPage}
                                        onChange={(e) => setJumpToPage(e.target.value)}
                                        className="w-16 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md text-xs shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 dark:bg-gray-800 dark:text-gray-100"
                                        min="1"
                                        max={pageCount}
                                    />
                                    <button
                                        type="submit"
                                        className="px-3 py-1 border rounded-md text-xs font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-sidebar-primary dark:hover:bg-indigo-700 hover:bg-gray-100 focus:outline-none"
                                    >
                                        Go
                                    </button>
                                </form>

                                {/* Navigation buttons */}
                                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                                    <button
                                        onClick={() => setPagination((p) => ({ ...p, pageIndex: 0 }))}
                                        disabled={pagination.pageIndex === 0}
                                        className="px-2 py-2 rounded-l-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-xs text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
                                    >
                                        <ChevronsLeftIcon />
                                    </button>
                                    <button
                                        onClick={() =>
                                            setPagination((p) => ({ ...p, pageIndex: p.pageIndex - 1 }))
                                        }
                                        disabled={pagination.pageIndex === 0}
                                        className="px-2 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-xs text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
                                    >
                                        <ChevronLeftIcon />
                                    </button>
                                    <button
                                        onClick={() =>
                                            setPagination((p) => ({ ...p, pageIndex: p.pageIndex + 1 }))
                                        }
                                        disabled={pagination.pageIndex >= pageCount - 1}
                                        className="px-2 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-xs text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
                                    >
                                        <ChevronRightIcon />
                                    </button>
                                    <button
                                        onClick={() =>
                                            setPagination((p) => ({ ...p, pageIndex: pageCount - 1 }))
                                        }
                                        disabled={pagination.pageIndex >= pageCount - 1}
                                        className="px-2 py-2 rounded-r-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-xs text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
                                    >
                                        <ChevronsRightIcon />
                                    </button>
                                </nav>
                            </div>
                        </div>
                    </div>
                )
            }
        </div >
    );
};